import { useSearchParams } from 'react-router';

import { FINANCING_PARTNERS, type FinancingPartner } from '../lib/financing-partners';

export type EligibilityFilter = 'all' | 'eligible' | 'not_eligible';
export type PartnerFilter = 'all' | FinancingPartner;
export type AssignedFilter = 'all' | FinancingPartner | 'unassigned';
export type WindowFilter = 'all' | '30' | '60' | '90' | '180';

export const DEFAULT_WINDOW: WindowFilter = '90';

export interface ActiveDealFilters {
  q: string;
  eligibility: EligibilityFilter;
  partner: PartnerFilter;
  assigned: AssignedFilter;
  window: WindowFilter;
}

const VALID_ELIGIBILITY = new Set<string>(['all', 'eligible', 'not_eligible']);
const VALID_PARTNERS = new Set<string>(['all', ...FINANCING_PARTNERS]);
const VALID_ASSIGNED = new Set<string>(['all', 'unassigned', ...FINANCING_PARTNERS]);
const VALID_WINDOWS = new Set<string>(['all', '30', '60', '90', '180']);

function parseParam<T extends string>(value: string | null, valid: Set<string>, fallback: T): T {
  return value !== null && valid.has(value) ? (value as T) : fallback;
}

function writeParam(
  params: URLSearchParams,
  key: string,
  value: string | undefined,
  defaultValue: string,
): void {
  if (value === undefined) return;
  if (value === defaultValue || value === '') {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}

export function useActiveDealFilters(): [
  ActiveDealFilters,
  (patch: Partial<ActiveDealFilters>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ActiveDealFilters = {
    q: searchParams.get('q') ?? '',
    eligibility: parseParam<EligibilityFilter>(
      searchParams.get('eligibility'),
      VALID_ELIGIBILITY,
      'all',
    ),
    partner: parseParam<PartnerFilter>(searchParams.get('partner'), VALID_PARTNERS, 'all'),
    assigned: parseParam<AssignedFilter>(searchParams.get('assigned'), VALID_ASSIGNED, 'all'),
    window: parseParam<WindowFilter>(searchParams.get('window'), VALID_WINDOWS, DEFAULT_WINDOW),
  };

  function setFilters(patch: Partial<ActiveDealFilters>): void {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        writeParam(updated, 'q', patch.q, '');
        writeParam(updated, 'eligibility', patch.eligibility, 'all');
        writeParam(updated, 'partner', patch.partner, 'all');
        writeParam(updated, 'assigned', patch.assigned, 'all');
        writeParam(updated, 'window', patch.window, DEFAULT_WINDOW);
        return updated;
      },
      { replace: true },
    );
  }

  return [filters, setFilters];
}
